import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import VideocamIcon from '@mui/icons-material/Videocam';
import
    {
        Avatar,
        Box,
        Button,
        CardContent,
        Chip,
        IconButton,
        Stack,
        Typography,
        useMediaQuery,
        useTheme,
    } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { EEmployeeTalkStatus } from '@sharedInterfaces/IEmployeeTalk';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getEmployeeTalks from '@src/APIs/graphQl/EmployeeTalk/getEmployeeTalks';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import StatusChip from '@src/App/Components/General/StatusChip/StatusChip';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import { Ellipsis } from 'lucide-react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function ETManagerWidget()
{
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const currentUser = useSelector((state: AppState) => state.employee);
    const employeeTalks = useSelector((state: AppState) => state.employeeTalks);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    useEffect(() =>
    {
        if (employeeTalks.length === 0)
        {
            getEmployeeTalks();
        }
    }, [employeeTalks.length]);

    // Manager sieht sowohl seine eigenen Talks als auch die, die er als Manager führt
    const myEmployeeTalks = employeeTalks.filter((talk) => talk.employeeId === currentUser.id);
    const managedTalks = employeeTalks.filter((talk) => talk.meetingPartnerId === currentUser.id);
    const allRelevantTalks = [...myEmployeeTalks, ...managedTalks];

    // Remove duplicates if any
    const uniqueTalks = allRelevantTalks.filter(
        (talk, index, self) => index === self.findIndex((t) => t.id === talk.id),
    );

    const upcomingTalks = uniqueTalks.filter((talk) => talk.status2 === EEmployeeTalkStatus.PLANNED);
    const recentTalks = uniqueTalks.slice(0, 4);

    const getStatusColor = (status: EEmployeeTalkStatus) =>
    {
        switch (status)
        {
            case EEmployeeTalkStatus.PLANNED:
                return 'pending';
            case EEmployeeTalkStatus.COMPLETED:
                return 'success';
            case EEmployeeTalkStatus.CANCELLED:
                return 'error';
            case EEmployeeTalkStatus.POSTPONED:
                return 'warning';
            default:
                return 'default';
        }
    };

    const getEmployeeName = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        return employee ? employee.title : `ID: ${employeeId}`;
    };

    const renderEmployeePicture = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        if (!employee) return null;

        const parts = (employee.title ?? '').trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

        return (
            <EmployeePicture
                employee={{
                    id: employee.id,
                    firstName,
                    lastName,
                    pictureId: employee.pictureId || null,
                }}
                avatarSize={32}
            />
        );
    };

    const formatDate = (dateString?: string) =>
    {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const isMyTalk = (talk: any) => talk.employeeId === currentUser.id;
    const isManagedTalk = (talk: any) => talk.meetingPartnerId === currentUser.id;

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.background.paper,
                }}
            >
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    Employee Talks
                </Typography>
                <Chip label={`${upcomingTalks.length} upcoming`} size="small" color="primary" />
                <IconButton size="small" sx={{ ml: 'auto' }}>
                    <Ellipsis size={20} />
                </IconButton>
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                    <Stack spacing={1.5}>
                        {recentTalks.length > 0 ? (
                            recentTalks.map((talk) =>
                            {
                                const isOwn = isMyTalk(talk);
                                const isManaged = isManagedTalk(talk);
                                const displayEmployeeId = isOwn ? talk.meetingPartnerId : talk.employeeId;
                                return (
                                    <Box
                                        key={talk.id}
                                        sx={{
                                            display: isMobile ? 'block' : 'flex',
                                            alignItems: isMobile ? 'stretch' : 'center',
                                            gap: 2,
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                        }}
                                        onClick={() => navigate(`/${ELinks.EMPLOYEE_TALKS}/${talk.id}`)}
                                    >
                                        {!isOwn && renderEmployeePicture(displayEmployeeId)}
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {talk.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {getEmployeeName(displayEmployeeId)}
                                                </Typography>
                                            </Box>
                                            {isOwn && (
                                                <Chip
                                                    label="Eigenes Gespräch"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: isMobile ? 1 : 0 }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(talk.scheduledDate)}
                                            </Typography>
                                            <StatusChip
                                                status={
                                                    getStatusColor(talk.status2) === 'success'
                                                        ? 'success'
                                                        : getStatusColor(talk.status2) === 'error'
                                                            ? 'error'
                                                            : getStatusColor(talk.status2) === 'warning'
                                                                ? 'warning'
                                                                : 'info'
                                                }
                                                label={talk.status2}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Keine Mitarbeitergespräche gefunden
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(`/${ELinks.EMPLOYEE_TALKS}`)}
                    sx={{ mt: 'auto', textTransform: 'none', fontWeight: 600 }}
                >
                    {langStrings.oftenUsed.viewAll}
                </Button>
            </CardContent>
        </BaseWidget>
    );
}
